import { useMemo } from "react";

import { useCustomNotification } from "design-system/hooks";
import {
  type AuditLogRequestInput,
  useAuditLogRequestInputMutation,
  useGetRegionsQuery,
} from "generated/graphql";
import { featureFlags } from "shell/platform/config/featureFlags";
import {
  AWS_REGIONS,
  DEFAULT_AWS_REGION,
  INTERNAL_ROLES,
  STP_REGION_ID,
} from "shell/platform/constants/constants";
import { useDecodedTokens } from "shell/platform/hooks/useDecodedTokens";
import { useLocalStorage } from "shell/platform/hooks/useLocalStorage";
import { useRoles } from "shell/platform/hooks/useRoles";
import { client } from "shell/platform/lib/graphqlClient";
import {
  getCurrentRegion,
  setCurrentRegion,
} from "shell/platform/utils/regionalApolloClientUtils";

import { ConfigureModal } from "../shared/ConfigureModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface Region {
  defaultRegion?: boolean;
  regionDescription: string;
  regionId: number;
  regionName: string;
}

const REGION_ID_KEY = "regionData";

const STP_US_OPTION_ID = -1;
const STP_APAC_OPTION_ID = -2;

const STP_OPTION_NAMES: Record<number, string> = {
  [STP_US_OPTION_ID]: "STP US",
  [STP_APAC_OPTION_ID]: "STP APAC",
};

const getRegionName = (id: number, regions: Region[]) =>
  STP_OPTION_NAMES[id] ??
  regions.find(region => region.regionId === id)?.regionName ??
  "Unknown";

export const RegionConfigurationModal = ({ isOpen, onClose }: Props) => {
  const { data: regionsResponse } = useGetRegionsQuery();
  const allRegions = regionsResponse?.allRegions ?? [];
  const { accessToken } = useDecodedTokens();
  const { hasRole } = useRoles();
  const isInternal = hasRole(INTERNAL_ROLES);

  const regionsData: Region[] = useMemo(() => {
    if (!accessToken?.regions || accessToken.regions.length === 0) {
      return allRegions;
    }
    if (featureFlags.enableAccessTokenRegionFiltering) {
      return allRegions.filter(region =>
        accessToken.regions?.find(
          tokenRegion =>
            tokenRegion.region.toLowerCase() ===
            region.regionName.toLowerCase(),
        ),
      );
    }
    return allRegions;
    // oxlint-disable-next-line react/exhaustive-deps
  }, [allRegions, accessToken]);

  const currentDefaultRegionId =
    regionsData.find((region: Region) => region.defaultRegion)?.regionId ?? 0;
  const [storedRegionId, setStoredRegionId] = useLocalStorage(
    REGION_ID_KEY,
    currentDefaultRegionId,
  );
  const { showNotification } = useCustomNotification();
  const [createAuditLog] = useAuditLogRequestInputMutation();

  const formattedOptions = useMemo(() => {
    const baseOptions = regionsData.map((region: Region) => ({
      id: region.regionId,
      name: region.regionName,
      description: region.regionDescription,
    }));

    if (!isInternal) return baseOptions;

    return [
      ...baseOptions,
      {
        id: STP_US_OPTION_ID,
        name: "STP US",
        description: "STP US",
      },
      {
        id: STP_APAC_OPTION_ID,
        name: "STP APAC",
        description: "STP APAC",
      },
    ];
  }, [regionsData, isInternal]);

  const activeAwsRegion = getCurrentRegion();
  const selectedValue =
    activeAwsRegion === AWS_REGIONS.US_EAST_2
      ? STP_US_OPTION_ID
      : activeAwsRegion === AWS_REGIONS.AP_SOUTHEAST_1
        ? STP_APAC_OPTION_ID
        : storedRegionId;

  const handleRegionChange = async (value: number) => {
    const oldRegionName = getRegionName(selectedValue, regionsData);
    const newRegionName = getRegionName(value, regionsData);

    const input: AuditLogRequestInput = {
      screenName: "ChangeRegion",
      details: `Changed region from ${oldRegionName} to ${newRegionName}`,
    };

    try {
      const response = await createAuditLog({
        variables: { input },
      });

      if (!response.data) {
        showNotification({
          type: "error",
          message: "Error",
          description: "An error occurred while logging the change.",
        });
      }

      if (value === STP_US_OPTION_ID) {
        setCurrentRegion(AWS_REGIONS.US_EAST_2);
        setStoredRegionId(STP_REGION_ID);
      } else if (value === STP_APAC_OPTION_ID) {
        setCurrentRegion(AWS_REGIONS.AP_SOUTHEAST_1);
        setStoredRegionId(STP_REGION_ID);
      } else {
        setCurrentRegion(DEFAULT_AWS_REGION);
        setStoredRegionId(value);
      }

      await client.resetStore();
    } catch (e: any) {
      showNotification({
        type: "error",
        message: "Error",
        description: e.message || "An error occurred while logging the change.",
      });
    }
  };

  return (
    <ConfigureModal
      isOpen={isOpen}
      onClose={onClose}
      title="Configure Region"
      value={selectedValue}
      onChange={handleRegionChange}
      options={formattedOptions}
      type="Region"
    />
  );
};
